'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { HiBars3, HiXMark } from 'react-icons/hi2';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog } from '@headlessui/react';
import { FaQuestionCircle } from 'react-icons/fa';

import dynamic from 'next/dynamic';

// Dynamiczny import GuidedTour (tylko client-side)
const GuidedTour = dynamic(() => import('./GuidedTour'), { ssr: false });

const baseNavigation = [
  { name: 'Strona główna', href: '/', tourId: 'nav-home' },
  { name: 'Katalog szkoleń', href: '/training', tourId: 'nav-katalog' },
  { name: 'Kalendarz', href: '/?view=calendar#courses-table', forceCalendar: true, tourId: 'nav-kalendarz' },
  // Blog ukryty w nawigacji — strony pod /blog nadal działają, tylko link zdjęty z menu
  // { name: 'Blog', href: '/blog', tourId: 'nav-blog' },
  { name: 'O nas', href: '/about', tourId: 'nav-about' },
  { name: 'Kontakt', href: '/contact', tourId: 'contact' },
];

const motorowodneNavItem = {
  name: 'Motorowodne',
  href: '/motorowodne',
  tourId: 'nav-motorowodne',
  highlight: true
};

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDesktopNav, setShowDesktopNav] = useState(true);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [motorowodneVisible, setMotorowodneVisible] = useState<boolean | null>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navLinksRef = useRef<HTMLDivElement>(null);
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Fetch navbar config to check motorowodne visibility
  useEffect(() => {
    const fetchNavbarConfig = async () => {
      try {
        const res = await fetch('/api/navbar-config');
        const data = await res.json();
        setMotorowodneVisible(data.motorowodneVisible !== false);
      } catch (error) {
        // Default to visible on error
        setMotorowodneVisible(true);
      }
    };
    fetchNavbarConfig();
  }, []);

  // Build navigation array based on config (null = still loading, treat as hidden)
  const navigation = motorowodneVisible === true
    ? [...baseNavigation.slice(0, 2), motorowodneNavItem, ...baseNavigation.slice(2)]
    : baseNavigation;

  // Sprawdzanie czy przyciski nawigacyjne mieszczą się w kontenerze
  useEffect(() => {
    const checkNavFit = () => {
      if (navContainerRef.current && navLinksRef.current && logoContainerRef.current) {
        const containerWidth = navContainerRef.current.offsetWidth;
        const linksWidth = navLinksRef.current.offsetWidth;
        const logoWidth = logoContainerRef.current.offsetWidth;
        
        // Margines bezpieczeństwa dla elementów nawigacji
        const totalRequiredWidth = linksWidth + logoWidth + 140;
        
        // Sprawdź czy wszystkie elementy się mieszczą
        setShowDesktopNav(containerWidth >= totalRequiredWidth);
      }
    };
    
    // Inicjalne sprawdzenie
    checkNavFit();
    
    // Nasłuchiwanie na zmiany rozmiaru okna
    window.addEventListener('resize', checkNavFit);
    
    // Dodatkowe sprawdzenie po pełnym załadowaniu strony
    window.addEventListener('load', checkNavFit);
    
    // Sprawdzanie co 500ms przez pierwsze 2 sekundy (dla pewności)
    const intervalId = setInterval(checkNavFit, 500);
    setTimeout(() => clearInterval(intervalId), 2000);
    
    // Czyszczenie nasłuchiwania
    return () => {
      window.removeEventListener('resize', checkNavFit);
      window.removeEventListener('load', checkNavFit);
      clearInterval(intervalId);
    };
  }, []);

  // Blokowanie przewijania gdy menu mobilne jest otwarte
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('prevent-scroll');
    } else {
      document.body.classList.remove('prevent-scroll');
    }
    
    return () => {
      document.body.classList.remove('prevent-scroll');
    };
  }, [mobileMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, item: any) => {
    // Obsługa kliknięć w linki nawigacyjne
    if (item.forceCalendar) {
      e.preventDefault();
      
      // Sprawdź czy jesteśmy na stronie głównej
      if (window.location.pathname === '/') {
        // Ustaw widok kalendarza w sessionStorage
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('homeViewMode', 'calendar');
          
          // Zmień widok na kalendarz i przewiń do przełącznika widoku
          const viewModeEvent = new CustomEvent('setViewMode', { 
            detail: { 
              mode: 'calendar',
              scrollTo: true
            } 
          });
          window.dispatchEvent(viewModeEvent);
        }
      } else {
        // Jeśli jesteśmy na innej podstronie, przejdź na stronę główną z parametrem view
        sessionStorage.setItem('homeViewMode', 'calendar');
        
        // Ustaw flagę w sessionStorage, że zostaliśmy przekierowani z navbara
        sessionStorage.setItem('redirectedFromNavbar', 'true');
        
        // Zapisz informację, że chcemy przewinąć do kalendarza po załadowaniu strony
        sessionStorage.setItem('scrollToCalendarAfterLoad', 'true');
        
        // Przekieruj na stronę główną z parametrami
        window.location.href = '/?view=calendar';
      }
    }
  };

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Jeśli już jesteśmy na stronie głównej, zapobiegamy domyślnemu zachowaniu
    if (window.location.pathname === '/') {
      e.preventDefault();
      
      // Przewiń na górę z wielokrotnym wywołaniem dla pewności
      window.scrollTo(0, 0);
      setTimeout(() => window.scrollTo(0, 0), 50);
      setTimeout(() => window.scrollTo(0, 0), 100);
      setTimeout(() => window.scrollTo(0, 0), 200);
    }
    
    // Wyczyść wszystkie zapisane pozycje przewijania
    try {
      sessionStorage.removeItem('scrollPosition');
      sessionStorage.removeItem('lastVisitedSection');
      sessionStorage.removeItem('navigatingFromEvent');
      sessionStorage.removeItem('navigatingTime');
      sessionStorage.removeItem('forceScrollTop');
      sessionStorage.removeItem('scrollBeforeViewChange');
      sessionStorage.removeItem('scrollBeforeMonthChange');
      
      // Ustaw flagę wymuszającą przewijanie na górę przy wejściu na stronę główną
      sessionStorage.setItem('forceScrollTop', 'true');
    } catch (e) {
      // Ignoruj błędy dostępu do sessionStorage
    }
    
    // Zamknij menu mobilne jeśli jest otwarte
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  const handleMobileHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Zamknij menu mobilne
    setMobileMenuOpen(false);
    // Wywołaj standardową obsługę kliknięcia w link do strony głównej
    handleHomeClick(e);
  };

  const handleMobileNavClick = (e: React.MouseEvent<HTMLAnchorElement>, item: any) => {
    // Zamknij menu mobilne
    setMobileMenuOpen(false);
    
    // Wywołaj standardową obsługę kliknięcia w link nawigacyjny
    handleNavClick(e, item);
  };

  return (
    <header 
      ref={headerRef} 
      className="bg-[rgb(6,40,95)] shadow-md navbar-header"
      style={{ height: 'auto' }}
    >
      <div className="w-full h-full flex justify-center items-center" ref={navContainerRef}>
        <nav className="w-full h-full flex justify-center items-center" aria-label="Global">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex justify-center items-center h-full">
            <div className="w-full flex items-center justify-between h-full">
              {/* Logo i nazwa firmy */}
              <div ref={logoContainerRef} className="flex items-center z-20 navbar-logo-container h-full pr-1 sm:pr-1 md:pr-2 lg:pr-3 xl:pr-3 ml-0 sm:ml-0 md:ml-0 lg:ml-0 2xl:-ml-20 pl-0 sm:pl-0 md:pl-0 lg:pl-0 xl:pl-2">
                <Link href="/" className="flex items-center gap-1 sm:gap-1 md:gap-1.5 lg:gap-2 h-full py-0" onClick={handleHomeClick}>
                  <div className="relative flex-shrink-0 h-full aspect-square navbar-logo">
                    <Image
                      src="/images/logo.webp"
                      alt="Ośrodek Szkolenia Morskiego Logo"
                      fill
                      className="object-contain p-1"
                      priority
                    />
                  </div>
                  <span className="text-sm sm:text-sm md:text-base lg:text-lg xl:text-xl font-bold text-white whitespace-nowrap navbar-brand-text py-0 my-0">Ośrodek Szkolenia<span className="hidden xxs:inline"> Morskiego</span></span>
                </Link>
              </div>
              
              {/* Przyciski nawigacyjne */}
              {showDesktopNav && (
                <div className="flex items-center z-20 h-full pl-1 sm:pl-1.5 md:pl-2 lg:pl-3 xl:pl-4 pr-1 sm:pr-1.5 md:pr-2 lg:pr-3 xl:pr-4">
                  <div ref={navLinksRef} className="flex items-center h-full space-x-0.5 sm:space-x-0.5 md:space-x-1 lg:space-x-1 xl:space-x-2 nav-links-inner">
                    {navigation.map((item: any) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        data-tour={item.tourId}
                        onClick={(e) => {
                          if (item.name === 'Strona główna') {
                            handleHomeClick(e);
                          } else {
                            handleNavClick(e, item);
                          }
                        }}
                        className={`text-xs sm:text-xs md:text-sm lg:text-sm xl:text-base font-semibold text-white hover:text-gray-200 transition-colors px-1 sm:px-1.5 md:px-2 lg:px-2.5 xl:px-3 h-full flex items-center justify-center gap-1 hover:bg-[rgb(26,60,115)] transition-all duration-200 nav-link whitespace-nowrap ${
                          item.highlight ? 'bg-sky-600/30 hover:bg-sky-500/40 rounded-md' : ''
                        }`}
                      >
                        
                        {item.name}
                      </Link>
                    ))}
                    {/* Przycisk Pomoc — tymczasowo ukryty (decyzja redakcji 2026-05-06).
                        Aby przywrócić, odkomentuj poniższy blok i jego odpowiednik
                        w menu mobilnym (~30 linii niżej). */}
                    {/*
                    <button
                      onClick={() => setShowGuidedTour(true)}
                      data-tour="new-user-btn"
                      className="text-xs sm:text-xs md:text-sm lg:text-sm xl:text-base font-semibold text-white hover:text-gray-200 transition-colors px-1 sm:px-1.5 md:px-2 lg:px-2.5 xl:px-3 h-full flex items-center justify-center gap-1 hover:bg-[rgb(26,60,115)] transition-all duration-200 nav-link whitespace-nowrap"
                      title="Przewodnik po stronie"
                    >
                      <FaQuestionCircle className="text-xs" />
                      <span className="hidden lg:inline">Pomoc</span>
                    </button>
                    */}
                  </div>
                </div>
              )}
              
              {/* Przycisk menu mobilnego */}
              <div className={`flex z-20 items-center ${showDesktopNav ? 'hidden' : ''} h-full`}>
                <button
                  type="button"
                  className="inline-flex items-center justify-center p-2 sm:p-2.5 md:p-3 text-white hover:bg-[rgb(16,50,105)] transition-colors mobile-menu-button h-full"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <span className="sr-only">Otwórz menu</span>
                  <HiBars3 className="h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 lg:h-10 lg:w-10" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop z animacją */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed inset-0 z-40 bg-black/50"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Menu panel z płynną animacją z prawej strony */}
            <motion.div 
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ 
                type: "spring",
                stiffness: 300,
                damping: 30,
                mass: 0.8
              }}
              className="fixed top-0 bottom-0 right-0 z-50 w-72 max-w-[80%] overflow-visible h-auto min-h-full bg-gradient-to-b from-[rgb(6,40,95)] to-[rgb(10,60,130)] px-6 py-6 shadow-lg mobile-menu-container flex flex-col"
            >
              {/* Przycisk zamknięcia i logo w górnej części */}
              <div className="flex-shrink-0 flex flex-col items-center mb-0 relative">
                <button
                  type="button"
                  className="rounded-md p-2 text-white hover:bg-[rgb(16,50,105)] absolute top-0 right-0"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sr-only">Zamknij menu</span>
                  <HiXMark className="h-6 w-6" aria-hidden="true" />
                </button>
                
                <Link href="/" className="flex justify-center mt-2" onClick={(e) => {
                  handleHomeClick(e);
                  setMobileMenuOpen(false);
                }}>
                  <div className="relative flex-shrink-0 w-14 h-14">
                    <Image
                      src="/images/logo.webp"
                      alt="Ośrodek Szkolenia Morskiego Logo"
                      fill
                      className="object-contain"
                      priority
                    />
                  </div>
                </Link>
              </div>
              
              <div className="flex-grow mobile-menu-items flex flex-col pt-0 pb-8">
                {navigation.map((item: any, index: number) => (
                  <motion.div
                    key={item.name}
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{
                      delay: index * 0.08,
                      duration: 0.3,
                      ease: "easeOut"
                    }}
                    className="my-0.5"
                  >
                    <Link
                      href={item.href}
                      data-tour={item.tourId}
                      onClick={(e) => {
                        if (item.name === 'Strona główna') {
                          handleHomeClick(e);
                        } else {
                          handleNavClick(e, item);
                        }
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-base font-semibold text-white hover:bg-[rgb(26,60,115)] transition-all duration-200 mobile-nav-link text-center ${
                        item.highlight ? 'bg-sky-600/30' : ''
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                {/* Przycisk Pomoc w menu mobilnym — tymczasowo ukryty
                    (decyzja redakcji 2026-05-06). */}
                {/*
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{
                    delay: navigation.length * 0.08,
                    duration: 0.3,
                    ease: "easeOut"
                  }}
                  className="my-0.5"
                >
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setTimeout(() => setShowGuidedTour(true), 300);
                    }}
                    className="w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-base font-semibold text-white hover:bg-[rgb(26,60,115)] transition-all duration-200"
                  >
                    <FaQuestionCircle />
                    Pomoc
                  </button>
                </motion.div>
                */}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Poradnik dla nowych użytkowników — montowany dopiero po kliknięciu (chunk react-joyride na żądanie) */}
      {showGuidedTour && <GuidedTour isOpen onClose={() => setShowGuidedTour(false)} />}
    </header>
  );
} 